import type {
  Channel,
  Filament,
  Nozzle,
  PackagingKit,
  PricingCalculateRequest,
  Settings,
  SlicerParseResult,
  StoredFile,
  Variant,
  VariantProfileInput,
} from '@deizi/shared';
import {
  bpsInput,
  durationInput,
  parseBps,
  parseCentavos,
  parseDecimal,
  parseDuration,
} from '../../lib/numbers';

/** One filament slot of a plate (from the sliced file, or a material typed by hand). */
export interface SlotDraft {
  key: string;
  /** Slicer filament number (file mode). */
  slot: number | null;
  type: string | null;
  color: string | null;
  /** Grams for the whole plate, as typed. */
  grams: string;
  filamentId: string | null;
  nozzle: Nozzle | null;
  /** The file said which nozzle feeds it; otherwise the user chooses. */
  nozzleFromFile: boolean;
  forSupport: boolean;
}

export interface PlateDraft {
  index: number;
  name: string | null;
  included: boolean;
  /** Plate print time, "h:mm". */
  time: string;
  unitsPerPlate: string;
  thumbnailFileId: string | null;
  slots: SlotDraft[];
}

export interface FileDraft {
  id: string;
  name: string;
  sizeBytes: number;
  format: SlicerParseResult['format'];
  printer: string | null;
  warnings: string[];
}

/** "Começar de um produto": the plates came from this catalog variant (no file attached). */
export interface SourceDraft {
  productId: string;
  variantId: string;
  label: string;
}

export interface Draft {
  mode: 'file' | 'manual';
  file: FileDraft | null;
  source: SourceDraft | null;
  description: string;
  printerId: string | null;
  filePlates: PlateDraft[];
  manualPlate: PlateDraft;
  quantity: string;
  finishingMin: string;
  packingMin: string;
  /** Packaging kit id, '' for none, null = not chosen yet (use the default kit). */
  kitId: string | null;
  supplies: { supplyId: string; qty: string }[];
  designHours: string;
  /** null = shop setting. */
  marginPct: string | null;
  failurePct: string | null;
  rushPct: string;
  discountPct: string;
  /** "Seu preço" per channel, as typed. */
  overrides: Record<string, string>;
  /** "Usar 25% neste canal": per-channel target margin (bps). */
  channelMargins: Record<string, number>;
  hidden: string[];
}

let seq = 0;
export const newKey = () => `k${Date.now().toString(36)}${(seq++).toString(36)}`;

export function emptySlot(filamentId: string | null = null): SlotDraft {
  return {
    key: newKey(),
    slot: null,
    type: null,
    color: null,
    grams: '',
    filamentId,
    nozzle: null,
    nozzleFromFile: false,
    forSupport: false,
  };
}

export function emptyDraft(): Draft {
  return {
    mode: 'file',
    file: null,
    source: null,
    description: '',
    printerId: null,
    filePlates: [],
    manualPlate: {
      index: 1,
      name: null,
      included: true,
      time: '',
      unitsPerPlate: '1',
      thumbnailFileId: null,
      slots: [emptySlot()],
    },
    quantity: '1',
    finishingMin: '3',
    packingMin: '5',
    kitId: null,
    supplies: [],
    designHours: '',
    marginPct: null,
    failurePct: null,
    rushPct: '0',
    discountPct: '0',
    overrides: {},
    channelMargins: {},
    hidden: [],
  };
}

const STORAGE_KEY = 'dz.qq.draft.v1';

/** The draft survives leaving the screen (sessionStorage; guarded, it may be unavailable). */
export function loadDraft(): Draft {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return Object.assign(emptyDraft(), JSON.parse(raw));
  } catch {
    // Private mode or blocked storage: start empty.
  }
  return emptyDraft();
}

export function saveDraft(draft: Draft): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // Ignore: the draft only lives in memory then.
  }
}

export function clearDraft(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore.
  }
}

/** Slot label as the boards write it: "Slot 2 · bico auxiliar · suporte". */
export function slotLabel(s: SlotDraft, i: number, mode: Draft['mode']): string {
  if (mode === 'manual') return `Material ${i + 1}`;
  const parts = [`Slot ${s.slot ?? i + 1}`];
  if (s.nozzle) parts.push(s.nozzle === 'main' ? 'bico principal' : 'bico auxiliar');
  if (s.forSupport) parts.push('suporte');
  return parts.join(' · ');
}

/** Plates from `POST /slicer/parse`: every sliced plate included by default. */
export function platesFromParse(result: SlicerParseResult): PlateDraft[] {
  return result.plates
    .filter((p) => p.sliced)
    .map((p) => ({
      index: p.index,
      name: p.name,
      included: true,
      time: durationInput(p.printSeconds ?? 0),
      unitsPerPlate: '1',
      thumbnailFileId: p.thumbnailFileId,
      slots: p.slots
        .filter((s) => s.grams > 0)
        .map((s) => ({
          key: newKey(),
          slot: s.slot,
          type: s.type,
          color: s.color,
          grams: String(s.grams).replace('.', ','),
          filamentId: s.suggestedFilamentId,
          nozzle: s.nozzle,
          nozzleFromFile: s.nozzle !== null,
          forSupport: s.forSupport,
        })),
    }));
}

export function fileDraft(file: StoredFile, result: SlicerParseResult): FileDraft {
  return {
    id: file.id,
    name: file.name,
    sizeBytes: file.sizeBytes,
    format: result.format,
    printer: result.printer.profile ?? result.printer.model,
    warnings: result.warnings,
  };
}

/** Item description: what was typed, else the file name without the slicer extension. */
export function describe(draft: Draft): string {
  if (draft.description.trim()) return draft.description.trim();
  if (draft.mode === 'file' && draft.file) {
    return draft.file.name
      .replace(/\.gcode\.3mf$|\.3mf$|\.gcode$/i, '')
      .replace(/[_-]+/g, ' ')
      .trim();
  }
  return '';
}

export function activePlates(draft: Draft): PlateDraft[] {
  return draft.mode === 'file' ? draft.filePlates.filter((p) => p.included) : [draft.manualPlate];
}

/** Default kit: the first one a channel uses by default, else the first kit. */
export function defaultKitId(kits: PackagingKit[]): string {
  return (kits.find((k) => k.defaultFor.length > 0) ?? kits[0])?.id ?? '';
}

export function quickQuoteChannels(channels: Channel[]): Channel[] {
  return channels.filter((c) => c.active && c.showInQuickQuote && c.current);
}

const intOf = (text: string, min: number) => {
  const n = parseDecimal(text);
  return n === null ? null : Math.max(min, Math.round(n));
};

export interface BuiltRequest {
  request: PricingCalculateRequest | null;
  /** Why there is no request yet (pt-BR, shown instead of prices). */
  missing: string | null;
}

/** Turns the draft into a `POST /pricing/calculate` body (stored printer, filaments, kit, channels). */
export function buildRequest(
  draft: Draft,
  ctx: {
    channels: Channel[];
    kits: PackagingKit[];
    filaments: Filament[];
    settings: Settings | undefined;
  },
): BuiltRequest {
  const plates = activePlates(draft);
  if (draft.mode === 'file' && !draft.file && !draft.source)
    return { request: null, missing: 'Envie o arquivo fatiado.' };
  if (plates.length === 0) return { request: null, missing: 'Inclua ao menos uma placa.' };
  if (!draft.printerId) return { request: null, missing: 'Escolha a impressora.' };

  const outPlates: PricingCalculateRequest['plates'] = [];
  for (const p of plates) {
    const seconds = parseDuration(p.time);
    if (seconds === null || seconds <= 0) {
      return { request: null, missing: 'Digite gramas e tempo para ver os preços.' };
    }
    const slots = [];
    for (const [i, s] of p.slots.entries()) {
      const grams = parseDecimal(s.grams);
      if (grams === null || grams <= 0) continue;
      if (!s.filamentId) {
        return {
          request: null,
          missing: `Escolha o filamento do ${draft.mode === 'manual' ? 'material' : 'slot'} ${s.slot ?? i + 1}.`,
        };
      }
      slots.push({ filamentId: s.filamentId, grams: Math.round(grams * 10) / 10 });
    }
    if (slots.length === 0)
      return { request: null, missing: 'Digite gramas e tempo para ver os preços.' };
    outPlates.push({ printSeconds: seconds, unitsPerPlate: intOf(p.unitsPerPlate, 1) ?? 1, slots });
  }

  const kitId = draft.kitId ?? defaultKitId(ctx.kits);
  const designHours = parseDecimal(draft.designHours) ?? 0;
  const margin = draft.marginPct === null ? null : parseBps(draft.marginPct);
  const failure = draft.failurePct === null ? null : parseBps(draft.failurePct);
  const channels = quickQuoteChannels(ctx.channels);
  if (channels.length === 0)
    return { request: null, missing: 'Cadastre um canal com taxas em vigor.' };

  return {
    missing: null,
    request: {
      printerId: draft.printerId,
      plates: outPlates,
      quantity: intOf(draft.quantity, 1) ?? 1,
      labor: {
        finishingSecondsPerUnit: Math.round((parseDecimal(draft.finishingMin) ?? 0) * 60),
        packingSecondsPerUnit: Math.round((parseDecimal(draft.packingMin) ?? 0) * 60),
      },
      design: designHours > 0 ? { seconds: Math.round(designHours * 3600) } : null,
      supplies: draft.supplies
        .map((s) => ({ supplyId: s.supplyId, quantityPerUnit: parseDecimal(s.qty) ?? 0 }))
        .filter((s) => s.quantityPerUnit > 0),
      ...(kitId ? { packagingKitId: kitId } : { packagingCentavos: 0 }),
      ...(margin !== null || failure !== null
        ? {
            rates: {
              ...(margin !== null ? { targetMarginBps: margin } : {}),
              ...(failure !== null ? { failureBps: failure } : {}),
            },
          }
        : {}),
      adjustments: {
        rushBps: parseBps(draft.rushPct) ?? 0,
        discountBps: Math.min(9900, parseBps(draft.discountPct) ?? 0),
      },
      channels: channels.map((c) => {
        const override = draft.overrides[c.id] ? parseCentavos(draft.overrides[c.id] ?? '') : null;
        return {
          channelId: c.id,
          ...(draft.channelMargins[c.id] !== undefined
            ? { targetMarginBps: draft.channelMargins[c.id] }
            : {}),
          ...(override !== null && override > 0 ? { overridePriceCentavos: override } : {}),
        };
      }),
    },
  };
}

/** Initial values for the adjustments from the shop settings. */
export function settingsPct(settings: Settings | undefined) {
  return {
    margin: bpsInput(settings?.costs.targetMarginBps ?? null),
    failure: bpsInput(settings?.costs.failureBps ?? null),
    minimumMarginBps: settings?.costs.minimumMarginBps ?? null,
  };
}

const minutes = (seconds: number) => String(Math.round((seconds / 60) * 10) / 10).replace('.', ',');

/** "Começar de um produto": the draft from a variant's print profile (plates, labor, materials). */
export function draftFromVariant(
  draft: Draft,
  product: { id: string; name: string },
  variant: Variant,
): Draft {
  const p = variant.profile;
  return {
    ...draft,
    mode: 'file',
    file: null,
    source: {
      productId: product.id,
      variantId: variant.id,
      label: `${product.name} ${variant.name}`,
    },
    description: `${product.name} ${variant.name}`,
    printerId: p.printerIds[0] ?? draft.printerId,
    filePlates: p.plates.map((plate, i) => ({
      index: i + 1,
      name: null,
      included: true,
      time: durationInput(plate.printSeconds),
      unitsPerPlate: String(plate.unitsPerPlate),
      thumbnailFileId: null,
      slots: plate.slots.map((s, j) => ({
        key: newKey(),
        slot: j + 1,
        type: null,
        color: null,
        grams: String(s.grams).replace('.', ','),
        filamentId: s.filamentId,
        nozzle: s.nozzle,
        nozzleFromFile: s.nozzle !== null,
        forSupport: false,
      })),
    })),
    finishingMin: minutes(p.labor.finishingSecondsPerUnit),
    packingMin: minutes(p.labor.packingSecondsPerUnit),
    kitId: p.packagingKitId ?? '',
    supplies: p.supplies.map((s) => ({
      supplyId: s.supplyId,
      qty: String(s.quantityPerUnit).replace('.', ','),
    })),
    overrides: {},
  };
}

/** "Salvar como produto": the variant's print profile from the draft (per plate, per unit). */
export function profileFromDraft(draft: Draft, kits: PackagingKit[]): VariantProfileInput {
  const kitId = draft.kitId ?? defaultKitId(kits);
  return {
    printerIds: draft.printerId ? [draft.printerId] : [],
    plates: activePlates(draft).map((p) => ({
      printSeconds: parseDuration(p.time) ?? 0,
      unitsPerPlate: intOf(p.unitsPerPlate, 1) ?? 1,
      slots: p.slots
        .map((s) => ({
          filamentId: s.filamentId,
          grams: Math.round((parseDecimal(s.grams) ?? 0) * 10) / 10,
          nozzle: s.nozzle,
        }))
        .filter((s) => s.grams > 0),
    })),
    labor: {
      finishingSecondsPerUnit: Math.round((parseDecimal(draft.finishingMin) ?? 0) * 60),
      packingSecondsPerUnit: Math.round((parseDecimal(draft.packingMin) ?? 0) * 60),
    },
    packagingKitId: kitId || null,
    supplies: draft.supplies
      .map((s) => ({ supplyId: s.supplyId, quantityPerUnit: parseDecimal(s.qty) ?? 0 }))
      .filter((s) => s.quantityPerUnit > 0),
  };
}
